import { Entity, Vector2, AttackHitbox } from './types'

export class Boss implements Entity {
  position: Vector2
  velocity: Vector2
  width = 80
  height = 100
  health = 300
  maxHealth = 300

  private phase = 1
  private attackTimer = 0
  private attackPattern = 0
  private moveTimer = 0
  private animFrame = 0
  private animTimer = 0
  private isAttacking = false
  private attackDuration = 0

  constructor(x: number, y: number) {
    this.position = { x, y }
    this.velocity = { x: 0, y: 0 }
  }

  update(
    deltaTime: number,
    playerPos: Vector2,
    groundY: number,
  ): AttackHitbox | null {
    this.attackTimer += deltaTime
    this.moveTimer += deltaTime
    this.animTimer += deltaTime
    this.attackDuration = Math.max(0, this.attackDuration - deltaTime)

    // Update phase based on health
    if (this.health < this.maxHealth * 0.66 && this.phase === 1) {
      this.phase = 2
    } else if (this.health < this.maxHealth * 0.33 && this.phase === 2) {
      this.phase = 3
    }

    const attackInterval = Math.max(1.5, 3 - this.phase * 0.5)

    // Movement AI
    if (this.moveTimer > 2) {
      const dx = playerPos.x - this.position.x
      const targetDistance = 200

      if (Math.abs(dx) > targetDistance) {
        this.velocity.x = dx > 0 ? 2 : -2
      } else {
        this.velocity.x *= 0.9
      }

      if (this.moveTimer > 3) {
        this.moveTimer = 0
      }
    }

    // Gravity
    this.velocity.y += 0.6

    // Update position
    this.position.x += this.velocity.x
    this.position.y += this.velocity.y

    // Ground collision
    if (this.position.y + this.height >= groundY) {
      this.position.y = groundY - this.height
      this.velocity.y = 0
    }

    // Screen bounds
    this.position.x = Math.max(0, Math.min(800 - this.width, this.position.x))

    // Attack patterns
    let hitbox: AttackHitbox | null = null
    if (this.attackTimer > attackInterval) {
      this.attackTimer = 0
      this.attackPattern = (this.attackPattern + 1) % 3
      this.isAttacking = true
      this.attackDuration = 0.3

      const dx = playerPos.x - this.position.x
      const direction = dx > 0 ? 1 : -1

      switch (this.attackPattern) {
        case 0: // Melee swipe
          hitbox = {
            position: {
              x: this.position.x + (direction > 0 ? this.width : -60),
              y: this.position.y + 20,
            },
            width: 60,
            height: 40,
            active: true,
            damage: 15,
          }
          break
        case 1: // Ground pound
          hitbox = {
            position: {
              x: this.position.x - 40,
              y: this.position.y + this.height - 20,
            },
            width: this.width + 80,
            height: 40,
            active: true,
            damage: 20,
          }
          break
        case 2: // Charge
          this.velocity.x = direction * 8
          hitbox = {
            position: {
              x: this.position.x,
              y: this.position.y,
            },
            width: this.width,
            height: this.height,
            active: true,
            damage: 25,
          }
          break
      }
    }

    // Animation
    if (this.animTimer > 0.15) {
      this.animFrame = (this.animFrame + 1) % 4
      this.animTimer = 0
    }

    return hitbox
  }

  render(ctx: CanvasRenderingContext2D) {
    ctx.save()
    ctx.translate(this.position.x, this.position.y)

    // Body - darker as health decreases
    const healthPercent = this.health / this.maxHealth
    const bodyColor = `rgb(${180 - healthPercent * 80}, ${50 + healthPercent * 30}, ${50 + healthPercent * 30})`
    ctx.fillStyle = bodyColor
    ctx.fillRect(10, 30, 60, 60)

    // Head
    ctx.fillStyle = '#8b0000'
    ctx.fillRect(20, 10, 40, 30)

    // Eyes - glow more as health decreases
    const eyeGlow = 1 - healthPercent
    ctx.fillStyle = `rgb(255, ${100 * eyeGlow}, 0)`
    ctx.fillRect(25, 18, 8, 8)
    ctx.fillRect(47, 18, 8, 8)

    // Horns
    ctx.fillStyle = '#4a0000'
    ctx.fillRect(15, 5, 8, 10)
    ctx.fillRect(57, 5, 8, 10)

    // Arms (animated)
    ctx.fillStyle = bodyColor
    const armSwing = Math.sin(this.animFrame) * 5
    ctx.fillRect(0, 40 + armSwing, 10, 30)
    ctx.fillRect(70, 40 - armSwing, 10, 30)

    // Attack effect
    if (this.attackDuration > 0) {
      ctx.fillStyle = 'rgba(255, 50, 50, 0.5)'
      ctx.fillRect(-20, -10, this.width + 40, this.height + 20)
    }

    // Phase indicator (aura)
    if (this.phase > 1) {
      ctx.strokeStyle = `rgba(255, 0, 0, ${0.3 * this.phase})`
      ctx.lineWidth = 3
      ctx.strokeRect(-5, -5, this.width + 10, this.height + 10)
    }

    ctx.restore()
  }

  takeDamage(amount: number): boolean {
    this.health = Math.max(0, this.health - amount)
    return this.health <= 0
  }

  reset(x: number, y: number) {
    this.position = { x, y }
    this.velocity = { x: 0, y: 0 }
    this.health = this.maxHealth
    this.phase = 1
    this.attackTimer = 0
    this.attackPattern = 0
    this.moveTimer = 0
  }
}
